import React, { useCallback, useEffect, useState } from "react";
import { Backpack, Check, Square, ShoppingBag, Trash2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { goAmazon } from "@/lib/amazonClick";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Unified feature-flag probe — cached across the app. Falls through
// to legacy behavior on any failure. 60s stale time is fine; the flag
// is env-driven so it doesn't flip mid-session.
function useUnifiedFlag() {
  const q = useQuery({
    queryKey: ["shopping-lists", "summary"],
    queryFn: async () => (await api.get("/shopping-lists/summary")).data,
    staleTime: 60_000,
    retry: false,
  });
  return !!q.data?.unified_flag;
}

/**
 * Backpack — per-trip shopping list.
 *
 * 2026-07-15 Bug #25 rev — when `SHOPPING_LISTS_UNIFIED=true`, this
 * component reads/writes the unified `user_shopping_items` collection
 * scoped to `trip_ids=[tripId]` so items added here appear on
 * `/marketplace/shopping-lists` under the trip pill (and under "All"),
 * and vice-versa. When the flag is OFF, we fall back to the legacy
 * per-trip endpoints unchanged.
 *
 * Legacy backend contract (flag OFF):
 *   GET    /api/booked-trips/{trip_id}/backpack
 *   POST   /api/booked-trips/{trip_id}/backpack            {name}
 *   PATCH  /api/booked-trips/{trip_id}/backpack/{item_id}  {name?, bought?}
 *   DELETE /api/booked-trips/{trip_id}/backpack/{item_id}
 *
 * Unified backend contract (flag ON):
 *   GET    /api/shopping-lists/all?trip_id={trip_id}
 *   POST   /api/shopping-lists/items       {name, trip_ids:[trip_id]}
 *   PATCH  /api/shopping-lists/items/{id}  {name? | checked?}
 *   DELETE /api/shopping-lists/items/{id}
 */

export const BACKPACK_TIDS = {
  section: (key) => `backpack-section-${key}`,
  input: (key) => `backpack-input-${key}`,
  addBtn: (key) => `backpack-add-btn-${key}`,
  item: (itemId) => `backpack-item-${itemId}`,
  toggle: (itemId) => `backpack-toggle-${itemId}`,
  amazon: (itemId) => `backpack-amazon-${itemId}`,
  remove: (itemId) => `backpack-remove-${itemId}`,
  wipe: (key) => `backpack-wipe-${key}`,
};

/**
 * Resolve the backpack REST endpoints for either flavor:
 *   • trip mode  →  `/booked-trips/{tripId}/backpack[/…]`
 *   • user-list  →  `/backpack/lists/{listId}/items[/…]`
 * Returned as builder functions so both flavors share the same
 * component code without conditional URLs sprinkled through it.
 */
function resolveLegacyEndpoints({ tripId, listId }) {
  if (listId) {
    const base = `/backpack/lists/${listId}/items`;
    return {
      key: `list-${listId}`,
      listUrl: base,
      addUrl: base,
      itemUrl: (id) => `${base}/${id}`,
      wipeUrl: null,
    };
  }
  const base = `/booked-trips/${tripId}/backpack`;
  return {
    key: tripId,
    listUrl: base,
    addUrl: base,
    itemUrl: (id) => `${base}/${id}`,
    wipeUrl: base,
  };
}

export default function BackpackList({ tripId, listId, mode = "full", onBought }) {
  const isStory = mode === "story";
  const qc = useQueryClient();
  const unified = useUnifiedFlag();
  // Unified mode only applies to per-trip Backpacks (tripId). Standalone
  // user lists (listId) stay on the legacy endpoints — those aren't
  // migrated yet.
  const useUnified = unified && !!tripId && !listId;
  const legacy = resolveLegacyEndpoints({ tripId, listId });
  const keyId = tripId || legacy.key;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);

  // Normalise both backends into a single UI shape: {id, name, bought}
  const _fromUnified = (row) => ({ id: row.id, name: row.name, bought: !!row.checked });

  const load = useCallback(async () => {
    try {
      if (useUnified) {
        const { data } = await api.get(`/shopping-lists/all?trip_id=${encodeURIComponent(tripId)}&limit=500`);
        setItems((data?.items || []).map(_fromUnified));
      } else {
        const { data } = await api.get(legacy.listUrl);
        setItems(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      if (e.response?.status !== 404) {
        console.error("[Backpack] load failed", e);
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [useUnified, tripId, legacy.listUrl]);

  useEffect(() => { load(); }, [load]);

  const invalidateShared = () => {
    // Broad-prefix invalidation — MarketplaceShoppingListsPage caches
    // under the same "shopping-lists" prefix, so this refetches the
    // marketplace surface immediately after per-trip mutations.
    if (useUnified) qc.invalidateQueries({ queryKey: ["shopping-lists"] });
  };

  const add = async (e) => {
    e.preventDefault();
    const name = text.trim();
    if (!name || adding) return;
    setAdding(true);
    try {
      if (useUnified) {
        const { data } = await api.post("/shopping-lists/items", {
          name,
          trip_ids: [tripId],
        });
        setItems((xs) => [...xs, _fromUnified(data.item)]);
        invalidateShared();
      } else {
        const { data } = await api.post(legacy.addUrl, { name });
        setItems((xs) => [...xs, data]);
      }
      setText("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't add item");
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (item) => {
    const next = !item.bought;
    setItems((xs) => xs.map((x) => (x.id === item.id ? { ...x, bought: next } : x)));
    try {
      if (useUnified) {
        await api.patch(`/shopping-lists/items/${item.id}`, { checked: next });
        invalidateShared();
      } else {
        await api.patch(legacy.itemUrl(item.id), { bought: next });
      }
      if (next && onBought) {
        try { onBought(item); } catch (e) { console.error("[Backpack] onBought handler failed", e); }
      }
    } catch (err) {
      setItems((xs) => xs.map((x) => (x.id === item.id ? { ...x, bought: item.bought } : x)));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update item");
    }
  };

  const remove = async (item) => {
    setItems((xs) => xs.filter((x) => x.id !== item.id));
    try {
      if (useUnified) {
        await api.delete(`/shopping-lists/items/${item.id}`);
        invalidateShared();
      } else {
        await api.delete(legacy.itemUrl(item.id));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't remove item");
      load();
    }
  };

  const wipe = async () => {
    if (!items.length) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      "Delete every item in this Backpack? This can't be undone.",
    );
    if (!ok) return;
    const snapshot = items;
    setItems([]);
    try {
      if (useUnified) {
        // No bulk delete in the unified API — fire in parallel.
        await Promise.all(snapshot.map((it) => api.delete(`/shopping-lists/items/${it.id}`)));
        invalidateShared();
      } else if (legacy.wipeUrl) {
        await api.delete(legacy.wipeUrl);
      }
      toast.success("Backpack cleared");
    } catch (err) {
      setItems(snapshot);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't clear backpack");
    }
  };

  const openAmazon = async (item) => {
    // 2026-07-15 #26: no per-item Amazon URL anymore — server generates
    // a tagged search URL from item.name.
    await goAmazon({ itemName: item.name, itemId: item.id });
  };

  const boughtCount = items.filter((i) => i.bought).length;

  return (
    <section
      data-testid={BACKPACK_TIDS.section(keyId)}
      className="rounded-2xl bg-white border border-tb-navy/15 p-3 shadow-[0_2px_10px_-4px_rgba(26,58,95,0.15)]"
    >
      <header className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-8 h-8 rounded-full flex items-center justify-center bg-tb-gold/20">
            <Backpack className="w-4 h-4 text-tb-gold-bright" />
          </span>
          <h3 className="font-serif-it text-tb-navy text-xl">Backpack</h3>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-hand text-tb-navy/65 text-xs italic">
            <b className="text-tb-blue not-italic">{boughtCount}</b> / {items.length}
          </span>
          {isStory && (useUnified || legacy.wipeUrl) && items.length > 0 && (
            <button
              type="button"
              data-testid={BACKPACK_TIDS.wipe(keyId)}
              onClick={wipe}
              className="text-xs font-hand text-tb-coral hover:text-tb-coral/80 underline underline-offset-2 tb-tap"
              title="Delete every item in this backpack"
            >
              Delete Backpack
            </button>
          )}
        </div>
      </header>

      {!isStory && (
        <form onSubmit={add} className="flex gap-2 mb-2">
          <input
            data-testid={BACKPACK_TIDS.input(keyId)}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add an item"
            className="flex-1 px-3 py-2 rounded-lg bg-white border border-tb-navy/20 focus:border-tb-navy outline-none font-ui text-sm text-tb-navy"
          />
          <button
            data-testid={BACKPACK_TIDS.addBtn(keyId)}
            type="submit"
            disabled={adding || !text.trim()}
            className="px-4 rounded-lg bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-sm tb-tap disabled:opacity-50"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "add"}
          </button>
        </form>
      )}

      {loading ? (
        <p className="font-hand text-tb-navy/55 italic text-center py-3 text-sm">
          {"loading backpack\u2026"}
        </p>
      ) : items.length === 0 ? (
        <p className="font-hand text-tb-navy/55 italic text-center py-3 text-sm">
          {isStory
            ? "no backpack items \u2014 nothing was added on this trip"
            : "nothing to buy yet \u2014 sunscreen? adaptor? snacks?"}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={BACKPACK_TIDS.item(it.id)}
              className={
                "flex items-center gap-2 rounded-xl bg-white border border-tb-navy/15 px-3 py-2 " +
                (it.bought ? "opacity-60" : "")
              }
            >
              <span
                className={
                  "flex-1 font-ui text-sm min-w-0 truncate " +
                  (it.bought ? "line-through text-tb-navy/45" : "text-tb-navy")
                }
                title={it.name}
              >
                {it.name}
              </span>
              <button
                type="button"
                data-testid={BACKPACK_TIDS.amazon(it.id)}
                onClick={() => openAmazon(it)}
                title={`Search Amazon for "${it.name}"`}
                className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-gold/20 text-tb-navy font-hand text-xs border border-tb-gold/60 hover:brightness-105 transition tb-tap"
              >
                <ShoppingBag className="w-3.5 h-3.5" />
                Buy on Amazon
              </button>
              <button
                type="button"
                data-testid={BACKPACK_TIDS.toggle(it.id)}
                onClick={() => toggle(it)}
                aria-label={it.bought ? "Mark not bought" : "Mark bought"}
                aria-pressed={it.bought}
                className="tb-tap shrink-0 w-7 h-7 rounded-full flex items-center justify-center border-2 border-tb-navy/25 hover:border-tb-navy/50"
              >
                {it.bought
                  ? <Check className="w-4 h-4 text-tb-blue" strokeWidth={3} />
                  : <Square className="w-4 h-4 text-tb-navy/40" />}
              </button>
              <button
                type="button"
                data-testid={BACKPACK_TIDS.remove(it.id)}
                onClick={() => remove(it)}
                className="text-tb-navy/40 hover:text-tb-coral tb-tap shrink-0"
                aria-label="Remove"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {items.length > 0 && (
        <AffiliateDisclosure variant="amazon" className="mt-2" />
      )}
    </section>
  );
}
